import * as THREE from 'three';
import { cloth, leather, std } from '../materials.ts';
import { G, M, ring } from '../prims.ts';
import { figure } from './heroes.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The cleric's last road, chapter 3 (docs/sendas/clerigo.md, «La nieve»): Piet's family in the corral, painted
 * miniatures at rest in the straw (they keep still all night: no rig, so nothing walks them).
 *
 * - Piet's mother (`pietMother`): a shepherd woman of the terraces sitting in the straw, her legs under her skirt, a
 *   thick wool shawl over her shoulders and a slate headscarf; one hand on the blanket of the child beside her.
 * - His two little brothers (`pietKid`, `pietKidB`): asleep, curled under a wool blanket, only the head out with its
 *   knitted cap.
 *
 * Colors: cold greys, slate and undyed wool (never cream: the wool is a cold grey so it stays grey by the braziers).
 */

const SKIN = '#c99c84';
const WOOL = '#a9adb3';
const SLATE = '#3f4652';

const face = (h: THREE.Object3D, y: number, z: number, s = 1): void => {
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.012 * s, 5, 4), std('#141214'), h).position.set(k * 0.042 * s, y, z);
  const n = M(new THREE.ConeGeometry(0.016 * s, 0.04 * s, 5), std('#b98c74', { roughness: 0.85 }), h);
  n.position.set(0, y - 0.028 * s, z + 0.016 * s);
  n.rotation.x = Math.PI / 2 + 0.2;
};

/** Piet's mother, sitting in the straw. */
const mother = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 895);
  const skin = std(SKIN, { roughness: 0.88 });
  const dress = cloth('#4a4d55');
  const shawl = cloth(WOOL);
  const f = figure({ legs: dress, boots: leather('#2a221c'), torso: dress, shoulders: shawl, arms: shawl, forearms: dress, hands: skin, skin });
  c.g.add(f.fig);
  // Sitting: the legs forward on the straw, the body down, leaning a little to the child.
  f.rig.legL.rotation.x = -1.45;
  f.rig.legR.rotation.x = -1.35;
  if (f.rig.shinL) f.rig.shinL.rotation.x = 0.9;
  if (f.rig.shinR) f.rig.shinR.rotation.x = 1.1;
  f.fig.position.y = -0.62;
  f.rig.body.rotation.z = 0.12;
  f.rig.armR.rotation.x = -0.7;
  f.rig.armR.rotation.z = 0.25;
  f.rig.armL.rotation.x = -0.35;
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The skirt spread over her legs.
  const skirt = M(new THREE.SphereGeometry(0.3, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), dress, c.g);
  skirt.position.set(0, 0.05, 0.22);
  skirt.scale.set(1, 0.42, 1.25);
  // The shawl: a thick wool wrap round the shoulders, its ends crossed.
  ring(b, 0.21, 0.07, shawl, 1.38, 6, 14);
  const end = M(new THREE.BoxGeometry(0.34, 0.24, 0.05), shawl, b);
  end.position.set(0, 1.22, 0.17);
  end.rotation.x = -0.15;
  // The headscarf, slate, tied under the chin.
  const scarf = cloth(SLATE);
  M(new THREE.SphereGeometry(0.138, 12, 9, 0, Math.PI * 2, 0, Math.PI * 0.6), scarf, h).position.set(0, 1.655, -0.015);
  M(new THREE.ConeGeometry(0.06, 0.12, 6), scarf, h).position.set(0, 1.56, -0.12);
  face(h, 1.656, 0.114);
  return done(c, 1.15);
};

/** A child of the terraces asleep under a wool blanket. */
const kid = (o: BaseOpts | undefined, seed: number, cap: string, yaw: number): Model => {
  const c = begin(o, 0.45, seed);
  const g = G(c.g, 0, 0, 0);
  g.rotation.y = yaw;
  // The blanket: a long low mound of grey wool, a darker stripe across it.
  const blanket = cloth('#7d838c');
  const mound = M(new THREE.SphereGeometry(0.3, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), blanket, g);
  mound.position.set(0, 0, -0.05);
  mound.scale.set(0.75, 0.55, 1.3);
  const stripe = M(new THREE.CylinderGeometry(0.235, 0.235, 0.07, 12, 1, true, -Math.PI / 2, Math.PI), cloth(SLATE), g);
  stripe.rotation.z = Math.PI / 2;
  stripe.rotation.y = Math.PI / 2;
  stripe.position.set(0, 0.0, 0.05);
  stripe.scale.set(0.72, 1, 0.72);
  // The head out at one end on a fold of the blanket, its knitted cap.
  const head = G(g, 0, 0.11, 0.38);
  M(new THREE.SphereGeometry(0.1, 12, 9), std(SKIN, { roughness: 0.85 }), head);
  const capM = cloth(cap);
  M(new THREE.SphereGeometry(0.104, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), capM, head).position.y = 0.012;
  M(new THREE.SphereGeometry(0.035, 6, 5), capM, head).position.set(0, 0.11, -0.02);
  // Asleep: the eyes shut (two short dark lines).
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.03, 0.006, 0.006), std('#2a2220'), head).position.set(k * 0.035, 0.0, 0.095);
  head.rotation.x = -0.25;
  const tick = (n: number): void => {
    // Breathing.
    mound.scale.y = 0.55 + Math.sin(n * 1.6 + seed) * 0.02;
  };
  return done(c, 0.5, tick);
};

/** The cleric's chapter 3 people by name (null: not one of them). */
export const nieveModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'pietMother':
      return mother(o);
    case 'pietKid':
      return kid(o, 896, '#5a3a36', 0.3);
    case 'pietKidB':
      return kid(o, 897, SLATE, -0.4);
    default:
      return null;
  }
};
